import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SvgIconProps } from '@mui/material';

export interface Option {
  label: string;
  value: string;
  disabled?: boolean;
}

interface DropDownFilterProps {
  placeholder?: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  tabKey: string;
  maxMenuHeight?: number;
  showSelectAll?: boolean;
  disabled?: boolean;
}

// const CustomArrow: React.FC<{ open?: boolean }> = ({ open }) => 

// CustomArrow
// CustomArrow
// ✅ Pure custom arrow icon (no manual onClick)
const CustomArrow: React.FC<{ open?: boolean }> = ({ open }) => (
  <Box
    sx={{
      width: 11,
      height: 10,
      borderRight: '2px solid #555',
      borderBottom: '2px solid #555',
      transform: open ? 'rotate(-135deg)' : 'rotate(45deg)',
      transition: 'transform 0.2s ease',
      position: 'absolute',
      right: 15,
      top: '38%',
      transformOrigin: 'center',
      pointerEvents: 'none', // ✅ don't block clicks
    }}
  />
);






const DropDownFilter: React.FC<DropDownFilterProps> = ({
  placeholder = 'Select...',
  options,
  value,
  onChange,
  tabKey,
  maxMenuHeight = 350,
  showSelectAll = true,
  disabled,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    const filteredOptions = options.filter(
      (o) =>
        (typeof o.label === 'string' ? o.label.toLowerCase().includes(q) : false) ||
        (typeof o.value === 'string' ? o.value.toLowerCase().includes(q) : false)
    );
    console.log('Filtered options:', filteredOptions, 'Search query:', q);
    return filteredOptions;
  }, [options, search]);

  const enabledValues = useMemo(
    () => options
      .filter((o) => !o.disabled && typeof o.value === 'string')
      .map((o) => o.value),
    [options]
  );

  const allSelected =
    enabledValues.length > 0 && enabledValues.every((v) => value.includes(v));
  const someSelected = value.length > 0 && !allSelected;
  console.log('DropDownFilter render:', { value, options, filtered, enabledValues, allSelected, someSelected });

  const handleSelectAll = (tabKey: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const nextValues = checked ? Array.from(new Set([...enabledValues])) : [];
    console.log('Select All clicked:', {
      tab: placeholder,
      tabKey,
      values: nextValues,
      action: checked ? 'Select All' : 'Uncheck All',
    });
    onChange(nextValues);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value as string[];
    console.log('DropDownFilter onChange:', next);
    onChange(next);
  };

  const renderValue = (selected: string[]) => {
    if (!selected.length) {
      return (
        <Typography
          sx={{
            color: 'text.secondary',
            width: '100%',
            textAlign: 'left',
          }}
        >
          {placeholder}
        </Typography>
      );
    }

    const selectedLabels = options
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label || o.value)
      .join(', ');

    return (
      <Typography
        sx={{
          maxWidth: 300,        // 🔹 restrict width
          whiteSpace: 'nowrap', // 🔹 single line
          overflow: 'hidden',   // 🔹 cut off overflow
          textOverflow: 'ellipsis', // 🔹 add "..."
          display: 'block',
        }}
      >
        {selectedLabels}
      </Typography>
    );
  };


  console.log('DropDownFilter props:', { options, value, placeholder, tabKey });

  return (
    <Tooltip
      title={
        value.length
          ? options
            .filter((o) => value.includes(o.value))
            .map((o) => o.label || o.value)
            .join(', ')
          : placeholder
      }
      placement="top"
      arrow
    >
      <FormControl fullWidth disabled={disabled}>
       <Select
  multiple
  displayEmpty
  value={value}
  onChange={handleChange}
  input={
    <OutlinedInput
      sx={{
        width: "100%",
        height: 53,
        overflow: 'hidden',
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          paddingY: 0,
          minWidth: '400px',
          position: 'relative',
        },
      }}
    />
  }
  renderValue={renderValue}
  MenuProps={{
    PaperProps: {
      sx: {
        borderRadius: "10px",
        overflow: "hidden",
        width: 370,
      },
    },
    MenuListProps: {
      sx: {
        maxHeight: maxMenuHeight,
        overflowY: "overlay",
        overflowX: "hidden",
        px: 0,
      },
    },
  }}
  IconComponent={(props: any) => <CustomArrow open={props.open} />} // ✅ no onClick
>

          {/* Search box */}
          <Box sx={{ px: 2, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                console.log('Search input:', e.target.value);
                setSearch(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Box>

          {/* Select All */}
          {showSelectAll && options.length > 0 && (
            <MenuItem dense>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(event) => handleSelectAll(tabKey, event)}
              />
              <ListItemText primary={allSelected ? 'Uncheck All' : 'Select All'} />
            </MenuItem>
          )}

          {/* Options with Tooltip */}
          {filtered.length ? (
            filtered.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                dense
              >
                <Checkbox checked={value.includes(opt.value)} />
                <Tooltip title={opt.label || opt.value} placement="right" arrow>
                  <ListItemText primary={opt.label || opt.value} />
                </Tooltip>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled dense>
              <ListItemText primary="No results" />
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default DropDownFilter;